// ============================================================================
// CANCUN100 — PDFComision.jsx (V2 — con fuentes reales y gradiente PNG)
// Componente del PDF de comisión confidencial (interno del agente)
// ============================================================================

import React from 'react';
import {
  Document, Page, Text, View, Image, StyleSheet,
} from '@react-pdf/renderer';
import './pdfFonts';
import { COLORS, fmtMXN } from './pdfStyles';
import gradientPng from './gradient-pink-orange.png';
import { lineaContacto, EMAIL, RP_VENTA_POR_FUERA } from '../contacto';

const styles = StyleSheet.create({
  page: {
    paddingTop: 32,
    paddingBottom: 130, // Bloque 38: hueco del pie fijo (absoluto) + numeración
    paddingHorizontal: 32,
    fontFamily: 'DM Sans',
    color: COLORS.textoOscuro,
    fontSize: 9,
  },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  brand: {
    fontSize: 32,
    color: COLORS.rosa,
    fontFamily: 'Space Grotesk',
    fontWeight: 700,
    letterSpacing: -1,
  },
  headerRight: { alignItems: 'flex-end' },
  reporteLabel: {
    fontSize: 7,
    color: COLORS.textoMedio,
    letterSpacing: 1.8,
    marginBottom: 2,
    fontFamily: 'DM Sans',
    fontWeight: 500,
  },
  folioComision: {
    fontSize: 24,
    color: COLORS.rosa,
    fontFamily: 'Space Grotesk',
    fontWeight: 700,
    marginBottom: 2,
    letterSpacing: -0.5,
  },
  fechaComision: { fontSize: 9, color: COLORS.textoMedio, fontFamily: 'DM Sans' },

  divisor: { height: 1.5, backgroundColor: COLORS.rosa, marginVertical: 8 },

  // ─── Badge "CUPÓN DE COMISIÓN INDIVIDUAL" (PNG gradient como fondo) ────
  badgeWrapper: {
    alignSelf: 'flex-start',
    borderRadius: 14,
    overflow: 'hidden',
    position: 'relative',
    marginBottom: 12,
    width: 215,
    height: 24,
  },
  badgeBg: { width: '100%', height: '100%' },
  badgeInner: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    fontSize: 8.5,
    color: COLORS.blanco,
    fontFamily: 'DM Sans',
    fontWeight: 700,
    letterSpacing: 1.3,
  },

  sectionTitle: {
    fontSize: 8.5,
    color: COLORS.rosa,
    fontFamily: 'DM Sans',
    fontWeight: 700,
    letterSpacing: 2,
    marginBottom: 8,
    marginTop: 6,
  },

  infoGrid: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 8 },
  infoItem: {
    width: '50%',
    paddingVertical: 4,
    paddingLeft: 8,
    borderLeftWidth: 2,
    borderLeftColor: COLORS.rosa,
    marginBottom: 6,
  },
  infoLabel: {
    fontSize: 7,
    color: COLORS.textoClaro,
    letterSpacing: 1,
    marginBottom: 2,
    fontFamily: 'DM Sans',
    fontWeight: 500,
  },
  infoValue: {
    fontSize: 10,
    color: COLORS.textoOscuro,
    fontFamily: 'DM Sans',
    fontWeight: 700,
  },
  infoFull: { width: '100%' },

  tableHeader: {
    flexDirection: 'row',
    backgroundColor: COLORS.fondoTabla,
    paddingVertical: 7,
    paddingHorizontal: 8,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: COLORS.bordeClaro,
  },
  thCell: {
    fontSize: 7,
    color: COLORS.textoMedio,
    fontFamily: 'DM Sans',
    fontWeight: 700,
    letterSpacing: 0.8,
  },
  colSku:     { width: '11%' },
  colServ:    { width: '34%' },
  colProv:    { width: '20%' },
  colOrig:    { width: '11%', textAlign: 'right' },
  colTc:      { width: '7%',  textAlign: 'right' },
  colPubMxn:  { width: '8%',  textAlign: 'right' },
  colNetoMxn: { width: '9%',  textAlign: 'right' },

  tableRow: {
    flexDirection: 'row',
    paddingVertical: 7,
    paddingHorizontal: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.bordeClaro,
  },
  tdCell: { fontSize: 8, color: COLORS.textoMedio, fontFamily: 'DM Sans' },
  tdSku: {
    fontSize: 8,
    color: COLORS.rosa,
    fontFamily: 'DM Sans',
    fontWeight: 700,
  },
  tdServ: {
    fontSize: 9,
    color: COLORS.textoOscuro,
    fontFamily: 'DM Sans',
    fontWeight: 700,
  },

  // ─── COMBO TOP TOURS (Xcaret) — desglose auditable interno ───────────
  comboBox: {
    borderWidth: 1,
    borderColor: '#ff8c42',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginTop: 10,
    marginBottom: 2,
    backgroundColor: '#fff8f0',
  },
  comboTitle: {
    fontSize: 8.5,
    color: '#d97706',
    fontFamily: 'DM Sans',
    fontWeight: 700,
    letterSpacing: 1.5,
    marginBottom: 5,
  },
  comboProductos: {
    fontSize: 8,
    color: COLORS.textoMedio,
    fontFamily: 'DM Sans',
    marginBottom: 6,
  },
  comboRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 1.5,
  },
  comboLabel: { fontSize: 9, color: COLORS.textoMedio, fontFamily: 'DM Sans' },
  comboValue: {
    fontSize: 9, color: COLORS.textoOscuro, fontFamily: 'DM Sans', fontWeight: 700,
  },
  comboNetoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 5,
    marginTop: 3,
    borderTopWidth: 0.5,
    borderTopColor: '#ffd9b3',
  },
  comboNetoText: {
    fontSize: 9.5, color: '#d97706', fontFamily: 'DM Sans', fontWeight: 700,
  },
  comboAhorro: {
    fontSize: 8.5, color: '#047857', fontFamily: 'DM Sans', fontWeight: 700,
    marginTop: 3,
  },

  desgloseRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.bordeClaro,
  },
  desgloseLabel: { fontSize: 10, color: COLORS.textoOscuro, fontFamily: 'DM Sans' },
  desgloseValue: {
    fontSize: 10,
    color: COLORS.textoOscuro,
    fontFamily: 'DM Sans',
    fontWeight: 700,
  },
  desgloseCargo: { backgroundColor: '#fff5e6' },
  desgloseCargoText: { color: '#d97706' },
  desgloseMargen: { backgroundColor: '#e6fff5' },
  desgloseMargenText: { color: '#047857', fontFamily: 'DM Sans', fontWeight: 700 },

  // ─── Comisión + Agencia: View con PNG gradient como fondo ───────────
  comisionWrapper: {
    marginTop: 14,
    borderRadius: 10,
    overflow: 'hidden',
    position: 'relative',
  },
  comisionBg: { width: '100%', height: 78 },
  comisionInner: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    flexDirection: 'row',
    padding: 14,
  },
  comisionCol: { flex: 1, justifyContent: 'center' },
  comisionLeftCol: { alignItems: 'flex-start' },
  comisionRightCol: { alignItems: 'flex-end' },
  comisionLabel: {
    fontSize: 8.5,
    color: COLORS.blanco,
    letterSpacing: 1.2,
    marginBottom: 4,
    fontFamily: 'DM Sans',
    fontWeight: 500,
  },
  comisionMain: {
    fontSize: 22,
    color: COLORS.blanco,
    fontFamily: 'Space Grotesk',
    fontWeight: 700,
    letterSpacing: -0.5,
  },

  resumenPagoGrid: { flexDirection: 'row', marginTop: 14 },
  resumenPagoCell: {
    flex: 1,
    borderWidth: 1,
    borderColor: COLORS.bordeClaro,
    borderRadius: 6,
    padding: 12,
    marginHorizontal: 3,
    alignItems: 'center',
  },
  resumenPagoLabel: {
    fontSize: 7,
    color: COLORS.textoClaro,
    letterSpacing: 1.2,
    marginBottom: 4,
    fontFamily: 'DM Sans',
    fontWeight: 500,
  },
  resumenPagoValue: {
    fontSize: 16,
    color: COLORS.textoOscuro,
    fontFamily: 'Space Grotesk',
    fontWeight: 700,
    letterSpacing: -0.3,
  },

  // Bloque 38: pie anclado abajo en TODAS las páginas (fixed + absolute). Mismos textos y tipografías.
  footer: {
    position: 'absolute',
    bottom: 26,
    left: 32,
    right: 32,
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: COLORS.bordeClaro,
    paddingTop: 10,
  },
  pageNumber: {
    position: 'absolute',
    bottom: 12,
    right: 32,
    fontSize: 7,
    color: COLORS.textoClaro,
    fontFamily: 'DM Sans',
  },
  // Bloque 38: encabezado compacto de una línea en las páginas 2+
  headerCompacto: {
    position: 'absolute',
    top: 12,
    left: 32,
    right: 32,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.bordeClaro,
    paddingBottom: 3,
  },
  headerCompactoBrand: {
    fontSize: 10,
    color: COLORS.rosa,
    fontFamily: 'Space Grotesk',
    fontWeight: 700,
    letterSpacing: -0.3,
  },
  headerCompactoInfo: {
    fontSize: 7,
    color: COLORS.textoMedio,
    fontFamily: 'DM Sans',
    fontWeight: 500,
    letterSpacing: 1,
  },
  footerBold: {
    fontSize: 9.5,
    color: COLORS.rosa,
    fontFamily: 'DM Sans',
    fontWeight: 700,
    marginBottom: 2,
  },
  footerInfo: {
    fontSize: 8,
    color: COLORS.textoClaro,
    marginBottom: 2,
    fontFamily: 'DM Sans',
  },
  confidencial: {
    fontSize: 8,
    color: COLORS.rosa,
    fontFamily: 'DM Sans',
    fontWeight: 700,
    backgroundColor: COLORS.rosaSuave,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 4,
    marginTop: 8,
    textAlign: 'center',
  },
});

const PDFComision = ({ data }) => {
  const composicion = `${data?.cliente?.adultos || 0} Adultos, ${data?.cliente?.menores || 0} Menores, ${data?.cliente?.infantes || 0} Infantes`;
  const comisionPct = data?.desglose?.comision_pct || 40;
  // Bloque 38: RP venta por fuera por la bandera guardada o por la línea de contacto.
  const esRP = !!data?.desglose?.es_rp_por_fuera || data?.desglose?.linea_contacto === RP_VENTA_POR_FUERA;

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>

        {/* Bloque 38: encabezado compacto fijo, solo de la página 2 en adelante */}
        <View fixed style={styles.headerCompacto} render={({ pageNumber }) => (pageNumber > 1 ? (
          <>
            <Text style={styles.headerCompactoBrand}>CANCUN100</Text>
            <Text style={styles.headerCompactoInfo}>REPORTE DE COMISION  ·  {data?.folio || '—'}  ·  {data?.fecha_creacion || ''}  ·  CONFIDENCIAL</Text>
          </>
        ) : null)} />

        <View style={styles.header}>
          <Text style={styles.brand}>CANCUN100</Text>
          <View style={styles.headerRight}>
            <Text style={styles.reporteLabel}>REPORTE DE COMISION</Text>
            <Text style={styles.folioComision}>{data?.folio || '—'}</Text>
            <Text style={styles.fechaComision}>{data?.fecha_creacion || ''}</Text>
            {/* FASE 1 recuperado: TC visible SOLO en PDF comisión (interno) */}
            {Array.isArray(data?.monedas_pdf) && data.monedas_pdf.includes('USD') && data?.tasa_usd_pdf && (
              <Text style={[styles.fechaComision, { fontSize: 9, marginTop: 2, opacity: 0.85 }]}>
                TC: 1 USD = {data.tasa_usd_pdf} MXN
              </Text>
            )}
            {Array.isArray(data?.monedas_pdf) && data.monedas_pdf.includes('EUR') && data?.tasa_eur_pdf && (
              <Text style={[styles.fechaComision, { fontSize: 9, marginTop: 1, opacity: 0.85 }]}>
                TC: 1 EUR = {data.tasa_eur_pdf} MXN
              </Text>
            )}
          </View>
        </View>

        <View style={styles.divisor} />

        <View style={styles.badgeWrapper} wrap={false}>
          <Image src={gradientPng} style={styles.badgeBg} />
          <View style={styles.badgeInner}>
            <Text style={styles.badgeText}>CUPON DE COMISION INDIVIDUAL</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>INFORMACION DE LA VENTA</Text>
        <View style={styles.infoGrid}>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>REPRESENTANTE</Text>
            <Text style={styles.infoValue}>{data?.representante?.nombre || '—'}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>TELEFONO</Text>
            <Text style={styles.infoValue}>{data?.representante?.telefono || '—'}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>CLIENTE</Text>
            <Text style={styles.infoValue}>{data?.cliente?.nombre || '—'}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>FECHA</Text>
            <Text style={styles.infoValue}>{data?.fecha_servicio || '—'}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>HOTEL</Text>
            <Text style={styles.infoValue}>{data?.cliente?.hotel || '—'}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>PAGO</Text>
            <Text style={styles.infoValue}>{data?.pago?.metodo || '—'}</Text>
          </View>
          <View style={[styles.infoItem, styles.infoFull]}>
            <Text style={styles.infoLabel}>COMPOSICION DEL GRUPO</Text>
            <Text style={styles.infoValue}>{composicion}</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle} minPresenceAhead={70}>
          SERVICIOS ({(data?.servicios || []).length}) — TODO EN MXN
        </Text>
        <View style={styles.tableHeader} minPresenceAhead={40}>
          <Text style={[styles.thCell, styles.colSku]}>SKU</Text>
          <Text style={[styles.thCell, styles.colServ]}>SERVICIO</Text>
          <Text style={[styles.thCell, styles.colProv]}>PROV.</Text>
          <Text style={[styles.thCell, styles.colOrig]}>ORIG</Text>
          <Text style={[styles.thCell, styles.colTc]}>TC</Text>
          <Text style={[styles.thCell, styles.colPubMxn]}>PUB MXN</Text>
          <Text style={[styles.thCell, styles.colNetoMxn]}>NETO MXN</Text>
        </View>
        {(data?.servicios || []).map((s, i) => (
          <View key={i} style={styles.tableRow} wrap={false}>
            <Text style={[styles.tdSku, styles.colSku]}>{s.sku}</Text>
            <Text style={[styles.tdServ, styles.colServ]}>
              {s.nombre}{s.es_combo ? ' (combo)' : ''}
              {/* A3.2 — Composición (a.m.i) usada para el neto de este producto. Naranja + * si es propia. */}
              <Text style={{ fontSize: 6, color: s.composicion_editada ? '#ff8c42' : COLORS.textoMedio }}>
                {'\n'}pax {s.pax || '—'}{s.composicion_editada ? ' *' : ''}{s.desglose ? `\n${s.desglose}` : ''}
              </Text>
            </Text>
            <Text style={[styles.tdCell, styles.colProv]}>{s.proveedor_nombre || '—'}</Text>
            <Text style={[styles.tdCell, styles.colOrig]}>
              {s.moneda_origen && s.moneda_origen !== 'MXN'
                ? `${s.moneda_origen} $${fmtMXN(s.precio_origen)}`
                : '—'}
            </Text>
            <Text style={[styles.tdCell, styles.colTc]}>
              {s.tasa_cambio ? Number(s.tasa_cambio).toFixed(2) : '—'}
            </Text>
            <Text style={[styles.tdServ, styles.colPubMxn]}>${fmtMXN(s.pub_mxn)}</Text>
            <Text style={[styles.tdCell, styles.colNetoMxn]}>${fmtMXN(s.neto_mxn)}</Text>
          </View>
        ))}
        {/* A3.2 — Leyenda: solo si algún producto lleva composición propia (lápiz). */}
        {(data?.servicios || []).some((s) => s.composicion_editada) && (
          <Text style={{ fontSize: 6, color: '#ff8c42', marginTop: 3 }}>
            * pax en naranja = composición interna propia del producto (distinta a la cabecera del grupo).
          </Text>
        )}

        {/* COMBO TOP TOURS (Xcaret) — desglose auditable. Solo si aplica. */}
        {data?.combo && (
          <View style={styles.comboBox} wrap={false}>
            <Text style={styles.comboTitle}>COMBO TOP TOURS APLICADO</Text>
            <Text style={styles.comboProductos}>
              Productos: {(data.combo.productos || []).map((p) => p.sku).join(', ') || '—'}
            </Text>
            <View style={styles.comboRow}>
              <Text style={styles.comboLabel}>Suma público × pax (MXN)</Text>
              <Text style={styles.comboValue}>${fmtMXN(data.combo.publico_original)}</Text>
            </View>
            <View style={styles.comboRow}>
              <Text style={styles.comboLabel}>−20% combo</Text>
              <Text style={styles.comboValue}>${fmtMXN(data.combo.despues_20)}</Text>
            </View>
            <View style={styles.comboRow}>
              <Text style={styles.comboLabel}>−30% paquete</Text>
              <Text style={styles.comboValue}>${fmtMXN(data.combo.despues_30)}</Text>
            </View>
            <View style={styles.comboNetoRow}>
              <Text style={styles.comboNetoText}>Nuevo neto del combo</Text>
              <Text style={styles.comboNetoText}>${fmtMXN(data.combo.despues_30)}</Text>
            </View>
            <View style={styles.comboRow}>
              <Text style={styles.comboAhorro}>Ahorro vs público</Text>
              <Text style={styles.comboAhorro}>−${fmtMXN(data.combo.ahorro)}</Text>
            </View>
          </View>
        )}

        {/* Bloque 38: el desglose no se parte entre páginas */}
        <View wrap={false}>
        <Text style={styles.sectionTitle}>DESGLOSE FINANCIERO (MXN)</Text>
        <View style={styles.desgloseRow}>
          <Text style={styles.desgloseLabel}>Precio de Venta</Text>
          <Text style={styles.desgloseValue}>MXN ${fmtMXN(data?.desglose?.precio_venta)}</Text>
        </View>
        {data?.desglose?.cargo_int > 0 && (
          <View style={[styles.desgloseRow, styles.desgloseCargo]}>
            <Text style={[styles.desgloseLabel, styles.desgloseCargoText]}>Cargo pago internacional (5%)</Text>
            <Text style={[styles.desgloseValue, styles.desgloseCargoText]}>− MXN ${fmtMXN(data.desglose.cargo_int)}</Text>
          </View>
        )}
        <View style={styles.desgloseRow}>
          <Text style={styles.desgloseLabel}>Costo Neto</Text>
          <Text style={styles.desgloseValue}>MXN ${fmtMXN(data?.desglose?.costo_neto)}</Text>
        </View>
        <View style={[styles.desgloseRow, styles.desgloseMargen]}>
          <Text style={[styles.desgloseLabel, styles.desgloseMargenText]}>Margen</Text>
          <Text style={[styles.desgloseValue, styles.desgloseMargenText]}>MXN ${fmtMXN(data?.desglose?.margen)}</Text>
        </View>
        </View>

        {data?.desglose?.tt ? (() => {
          // Bloque 4 (tt_v2): las dos comisiones del agente Top Tours. La suya resaltada;
          // la otra tenue. Sin ganancia de la casa ni comisión de la supervisora.
          const t = data.desglose.tt;
          const d = data.desglose;
          const esPremier = t.modalidad === 'premier';
          // Bloque 38: la fila APLICA imprime lo GUARDADO (comision_agente, pct_agente_tt); el cálculo
          // informativo solo entra si vienen vacíos. Nunca se recalcula la comisión del agente.
          const calculoInformativo = esRP ? Math.round(Math.max(0, (d.margen || 0) * 0.80) * 100) / 100 : esPremier ? t.comision_5050 : t.comision_becados;
          const comisionGanada = d.comision_agente ?? calculoInformativo;
          const pctAplica = d.pct_agente_tt != null ? Math.round(d.pct_agente_tt * 100) : (esRP ? 80 : t.pct);
          const etiquetaAplica = esRP ? `RP VENTA POR FUERA ${pctAplica}%` : `${esPremier ? '50-50' : 'Becado'} ${pctAplica}%`;
          const fila = (label, monto, suya, leyenda) => (
            <View key={label} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 9, paddingHorizontal: 12, marginTop: 6, borderRadius: 8, backgroundColor: suya ? '#fff1f5' : '#f7f7f7', borderWidth: suya ? 1.5 : 0.5, borderColor: suya ? COLORS.rosa : COLORS.bordeClaro }}>
              <View>
                <Text style={{ fontSize: suya ? 10.5 : 9, fontFamily: 'DM Sans', fontWeight: suya ? 700 : 400, color: suya ? COLORS.textoOscuro : COLORS.textoClaro }}>{label}{suya ? '   <- APLICA' : ''}</Text>
                {leyenda ? <Text style={{ fontSize: 7.5, fontFamily: 'DM Sans', color: COLORS.textoClaro, marginTop: 2 }}>{leyenda}</Text> : null}
              </View>
              <Text style={{ fontSize: suya ? 16 : 11, fontFamily: 'Space Grotesk', fontWeight: 700, color: suya ? COLORS.rosa : COLORS.textoClaro }}>MXN ${fmtMXN(monto)}</Text>
            </View>
          );
          return (
            <View style={{ marginTop: 10 }} wrap={false}>
              <Text style={styles.sectionTitle}>TU COMISION TOP TOURS</Text>
              {/* Bloque 17: la modalidad GUARDADA en el cupon (se congela al vender); la otra es solo informativa */}
              <View style={{ alignSelf: 'flex-start', marginTop: 4, marginBottom: 2, marginHorizontal: 12, paddingVertical: 4, paddingHorizontal: 10, borderRadius: 6, backgroundColor: COLORS.rosa }}>
                <Text style={{ fontSize: 10, fontFamily: 'DM Sans', fontWeight: 700, color: '#ffffff' }}>APLICA: {etiquetaAplica}</Text>
              </View>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, paddingHorizontal: 12 }}>
                <Text style={{ fontSize: 9.5, fontFamily: 'DM Sans', fontWeight: 700, color: COLORS.textoOscuro }}>Margen de la venta</Text>
                <Text style={{ fontSize: 11, fontFamily: 'Space Grotesk', fontWeight: 700, color: COLORS.textoOscuro }}>MXN ${fmtMXN(data?.desglose?.margen)}</Text>
              </View>
              {esRP ? (
                <>
                  {fila(`Comision ganada RP venta por fuera (${pctAplica}%)`, comisionGanada, true, null)}
                  {fila('Comision ganada Becado (30%)', t.comision_becados, false, 'Sin RP ganarias')}
                  {fila('Comision ganada 50-50 (50%)', t.comision_5050, false, 'Sin RP ganarias')}
                </>
              ) : (
                <>
                  {fila('Comision ganada Becado (30%)', esPremier ? t.comision_becados : comisionGanada, !esPremier, esPremier ? 'En Becados serias' : null)}
                  {fila('Comision ganada 50-50 (50%)', esPremier ? comisionGanada : t.comision_5050, esPremier, esPremier ? null : 'Asi ganarias en 50-50')}
                </>
              )}
            </View>
          );
        })() : (
        <View style={styles.comisionWrapper} wrap={false}>
          <Image src={gradientPng} style={styles.comisionBg} />
          <View style={styles.comisionInner}>
            <View style={[styles.comisionCol, styles.comisionLeftCol]}>
              <Text style={styles.comisionLabel}>{data?.desglose?.regla_admin ? `Regla contable: ${data.desglose.regla_admin}` : esRP ? 'Tu Comision RP (80%)' : `Tu Comision (${comisionPct}%)`}</Text>
              <Text style={styles.comisionMain}>MXN ${fmtMXN(data?.desglose?.comision_agente)}</Text>
            </View>
            <View style={[styles.comisionCol, styles.comisionRightCol]}>
              <Text style={styles.comisionLabel}>AGENCIA</Text>
              <Text style={styles.comisionMain}>MXN ${fmtMXN(data?.desglose?.ganancia_agencia)}</Text>
            </View>
          </View>
        </View>
        )}

        <View style={styles.resumenPagoGrid} wrap={false}>
          <View style={styles.resumenPagoCell}>
            <Text style={styles.resumenPagoLabel}>TOTAL</Text>
            <Text style={styles.resumenPagoValue}>${fmtMXN(data?.desglose?.total)}</Text>
          </View>
          <View style={styles.resumenPagoCell}>
            <Text style={styles.resumenPagoLabel}>DEPOSITO</Text>
            <Text style={styles.resumenPagoValue}>${fmtMXN(data?.desglose?.deposito)}</Text>
          </View>
          <View style={styles.resumenPagoCell}>
            <Text style={styles.resumenPagoLabel}>PENDIENTE</Text>
            <Text style={[styles.resumenPagoValue, { color: COLORS.rosa }]}>
              ${fmtMXN(data?.desglose?.pendiente)}
            </Text>
          </View>
        </View>

        <View wrap={false}>
        <Text style={styles.sectionTitle}>DATOS OPERATIVOS</Text>
        <View style={styles.infoGrid}>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>METODO DE PAGO</Text>
            <Text style={styles.infoValue}>{data?.pago?.metodo || '—'}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>NOTAS ADICIONALES</Text>
            <Text style={styles.infoValue}>{data?.pago?.notas || '—'}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>LINEA DE CONTACTO</Text>
            <Text style={styles.infoValue}>{data?.pago?.linea_contacto || '—'}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>PAGO DE BALANCE</Text>
            <Text style={styles.infoValue}>{data?.pago?.pago_balance || '—'}</Text>
          </View>
        </View>
        </View>

        {/* Bloque 38: pie fijo en todas las páginas + numeración "n / total" abajo a la derecha */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerBold}>
            cancun100.com{' '}
            <Text style={{ color: COLORS.textoMedio, fontFamily: 'DM Sans', fontWeight: 400 }}>
              by Xpectative Travel
            </Text>
          </Text>
          <Text style={styles.footerInfo}>
            {lineaContacto('sala').replace(`  |  ${EMAIL}`, '')}
          </Text>
          <Text style={styles.footerInfo}>{EMAIL}  |  @cancun100.site</Text>
          <Text style={[styles.footerInfo, { marginTop: 4, fontFamily: 'DM Sans', fontWeight: 700 }]}>
            Generado automaticamente - Cancun100
          </Text>
          <Text style={styles.confidencial}>
            CONFIDENCIAL - Uso exclusivo del representante. No compartir con clientes.
          </Text>
        </View>
        <Text style={styles.pageNumber} fixed render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </Page>
    </Document>
  );
};

export default PDFComision;
